import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] ring-ring/50",
	{
		variants: {
			variant: {
				// Filled primary variants disable into the design's muted
				// gray-blue (ZPL-344), not plain opacity.
				default:
					"bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 disabled:bg-primary-disabled disabled:text-primary-disabled-foreground disabled:shadow-none",
				destructive:
					"bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 disabled:opacity-50",
				outline:
					"border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
				secondary:
					"bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 disabled:opacity-50",
				ghost:
					"hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
				link: "text-primary underline-offset-4 hover:underline disabled:opacity-50",
				brand:
					"bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-all active:scale-95 disabled:bg-primary-disabled disabled:text-primary-disabled-foreground disabled:shadow-none",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 rounded-md px-3 text-xs has-[>svg]:px-2.5",
				lg: "h-10 rounded-md px-8 has-[>svg]:px-4",
				xl: "h-14 rounded-md px-12 text-lg font-bold",
				icon: "size-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
	}) {
	const Comp = asChild ? Slot : "button"

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button }
